import type { MachineField } from "./machine.ts";
import { machineWarning } from "./renderer-warning.ts";
import type { ChunkFigures, RowFigures } from "./stats.ts";

/**
 * The benchmark's report (P2.0-T4): one versioned, machine-readable record of a run, so later tools can compare runs
 * and draw the per-chunk figures over a map, and its plain text for a person, which is what "Copy results" copies:
 * the machine block first, then the table, the slowest chunks, and the record itself as one line of JSON at the end.
 * When the renderer that drew the run is integrated graphics or a software rasteriser, the text starts with one line
 * saying so (`renderer-warning.ts`); the record does not hold it, since the machine block it is read from does.
 */

/**
 * The report's format name and version.
 */
export const REPORT_FORMAT = "odyssey-bench-report";
export const REPORT_VERSION = 1;

/**
 * A run's report.
 */
export interface BenchReport {
  readonly format: typeof REPORT_FORMAT;
  readonly version: number;
  readonly machine: readonly MachineField[];
  readonly route: { readonly id: string; readonly format: number; readonly world: string; readonly map: string };
  readonly rows: readonly RowFigures[];
  readonly chunks: readonly ChunkFigures[];
}

/**
 * The table's columns: each header and how a row fills it.
 */
const COLUMNS: readonly (readonly [string, (row: RowFigures) => string])[] = [
  ["row", (row) => row.id],
  ["fps", (row) => String(row.fps)],
  ["p50 ms", (row) => String(row.p50)],
  ["p95 ms", (row) => String(row.p95)],
  ["p99 ms", (row) => String(row.p99)],
  ["longest ms", (row) => String(row.longest)],
  [">33 ms", (row) => String(row.over33)],
  ["crossings", (row) => String(row.crossings)],
  [">33 ms at crossings", (row) => String(row.crossingOver33)],
  ["longest at crossing", (row) => String(row.crossingLongest)],
  ["props drawn", (row) => String(row.props)],
  ["creatures drawn", (row) => String(row.creatures)],
  ["frames", (row) => String(row.frames)],
  ["note", (row) => row.note],
];

/**
 * How many of the slowest chunks the text lists.
 */
const SLOWEST_CHUNKS = 10;

/**
 * The report as text: the renderer warning when there is one, the machine block, the table with its columns padded,
 * the slowest chunks, and the JSON record.
 *
 * @param report The report.
 * @returns The text.
 */
export function reportText(report: BenchReport): string {
  const machine = report.machine.map((field) => `${field.key}: ${field.value}`);
  const cells = [COLUMNS.map(([header]) => header), ...report.rows.map((row) => COLUMNS.map(([, read]) => read(row)))];
  const widths = COLUMNS.map((_, column) => Math.max(...cells.map((line) => (line[column] as string).length)));
  const table = cells.map((line) => line.map((cell, column) => cell.padEnd(widths[column] as number)).join("  ").trimEnd());
  const chunks = report.chunks.slice(0, SLOWEST_CHUNKS).map((chunk) => `chunk ${chunk.cx},${chunk.cy}: p95 ${chunk.p95} ms, longest ${chunk.longest} ms, ${chunk.over33} frames over 33 ms of ${chunk.frames}`);
  const warning = machineWarning(report.machine);
  return [
    ...(warning === null ? [] : [warning, ""]),
    `OdysseyTales benchmark (${REPORT_FORMAT} v${report.version}, route ${report.route.id} v${report.route.format} on ${report.route.world}/${report.route.map})`,
    "",
    ...machine,
    "",
    ...table,
    "",
    `Slowest chunks by p95 frame time (of ${report.chunks.length}):`,
    ...chunks,
    "",
    "JSON:",
    JSON.stringify(report),
    "",
  ].join("\n");
}
